import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { SharedModels } from '@share-models';
import { PreloaderColor } from '@enums/shared.enum';
import { features } from '@consts/text.const';
import { AppState } from '../../../../../../store/app.state';
import { PersonGroupsActions } from '@states/person-groups/person-groups.actions';
import { personGroupsFeature } from '@states/person-groups/person-groups.feature';
import { PersonGroupsModel } from '@models/person-groups.model';
import { PersonModel } from '@models/person.model';
import { PersonGroupsDialogService } from '../../../../../../services/person-groups-dialog.service';
import { PersonService } from '../../../../../../services/person.service';
import { PersonDialogService } from '../../../../../../services/person-dialog.service';
import { UiButtonComponent, UiNoDataComponent, UiPersonTileComponent, UiPreloaderComponent, UiSelectedItemsSnackbarComponent } from '@ui';
import { Actions, ofType } from '@ngrx/effects';
import { filter, take } from 'rxjs';
import { PersonActions } from '@states/person/person.actions';

@Component({
  selector: 'app-person-group-details',
  imports: [
    TranslatePipe,
    FormsModule,
    UiButtonComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiPersonTileComponent,
    UiSelectedItemsSnackbarComponent,
    MatTooltip,
  ],
  templateUrl: './person-group-details.component.html',
  styleUrl: './person-group-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonGroupDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly store$ = inject(Store<AppState>);
  private readonly personGroupsDialogService = inject(PersonGroupsDialogService);
  private readonly personService = inject(PersonService);
  private readonly personDialogService = inject(PersonDialogService);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly loaderColor = PreloaderColor;
  protected readonly viewStates = SharedModels.ViewState;
  protected readonly noData = features.personGroup;
  protected readonly personTrainingStatus = PersonModel.TrainingStatus;

  protected readonly groupId = toSignal(
    this.route.paramMap.pipe(map((p) => p.get('groupId'))),
    { initialValue: this.route.snapshot.paramMap.get('groupId') },
  );

  // Person Group Details
  private readonly personGroupDetailsState = this.store$.selectSignal(personGroupsFeature.selectPersonGroupDetails);
  protected readonly personGroupDetails = computed(() => this.personGroupDetailsState().document);
  protected readonly isPersonGroupDetailsLoading = computed(() => this.personGroupDetailsState().isLoading);

  // Person Group Members
  private readonly personGroupMembersState = this.store$.selectSignal(personGroupsFeature.selectPersonGroupMembers);
  protected readonly members = this.store$.selectSignal(personGroupsFeature.selectAllPersonGroupMembers);
  protected readonly isPersonGroupMembersLoading = computed(() => this.personGroupMembersState().isLoading);

  protected readonly group = computed((): PersonGroupsModel.PersonGroupDetailsDocument | undefined => {
    const id = this.groupId();
    if (id == null || id === '') {
      return undefined;
    }
    const group = this.personGroupDetails();
    if (!group || group._id !== id) {
      return undefined;
    }
    return group;
  });

  /** Matches backend `canManageParticipants` (rename, etc.). */
  protected readonly canRenamePersonGroup = computed(
    () => this.group()?.activeOrgAccessRole === PersonGroupsModel.ShareAccessRole.Admin,
  );

  /** Matches backend `canEditGroup` (add/remove members). */
  protected readonly canEditGroupMembers = computed(() => {
    const role = this.group()?.activeOrgAccessRole;
    return (
      role === PersonGroupsModel.ShareAccessRole.Admin ||
      role === PersonGroupsModel.ShareAccessRole.Contributor
    );
  });

  protected readonly membersViewState = computed<SharedModels.ViewState>(() => {
    if (this.isPersonGroupDetailsLoading() || this.isPersonGroupMembersLoading()) {
      return SharedModels.ViewState.INITIAL_EMPTY;
    }
    return this.members().length > 0
      ? SharedModels.ViewState.HAS_CONTENT
      : SharedModels.ViewState.INITIAL_EMPTY;
  });

  protected readonly selectedPersonIds = signal<Set<number>>(new Set<number>());
  protected readonly selectedPersonsCount = computed(() => this.selectedPersonIds().size);
  protected readonly confirmingPersonIds = this.store$.selectSignal(
    personGroupsFeature.selectPersonGroupMemberConfirmingIds,
  );
  protected readonly removingPersonIds = this.store$.selectSignal(
    personGroupsFeature.selectPersonGroupMemberRemovingIds,
  );

  public ngOnInit(): void {
    const id = this.groupId();
    if (!id) {
      return;
    }
    this.store$.dispatch(PersonGroupsActions.startLoadingPersonGroupDetails({ groupId: id }));
  }

  protected getImagePath(person: PersonModel.SavedPerson, gcp: boolean = true): string {
    return this.personService.getPersonImagePath(person.bestImage, true, gcp);
  }

  protected editName(): void {
    const g = this.group();
    if (!g) {
      return;
    }
    this.personGroupsDialogService.openEdit(g._id, g.name);
  }

  protected shareGroup(): void {
    const g = this.group();
    if (!g) {
      return;
    }
    this.personGroupsDialogService.openShare(g._id, g.name, g.createdByOrgId);
  }

  protected addPersons(): void {
    if (!this.canEditGroupMembers()) {
      return;
    }
    const g = this.group();
    if (!g) {
      return;
    }
    this.personGroupsDialogService.openAddPersonsToGroup(g._id, g.name, this.members().map((m) => m.personId));
  }

  protected openEditPersonDialog(person: PersonModel.SavedPerson): void {
    if (!this.canEditGroupMembers()) {
      return;
    }
    this.personDialogService.openEditPerson(person, { disableClose: false });
  }

  protected isPersonSelected(personId: number): boolean {
    return this.selectedPersonIds().has(personId);
  }

  protected updatePersonSelection(personId: number, selected: boolean): void {
    if (!this.canEditGroupMembers()) {
      return;
    }
    this.selectedPersonIds.update((selectedPersonIds) => {
      const nextSelectedPersonIds = new Set(selectedPersonIds);
      if (selected) {
        nextSelectedPersonIds.add(personId);
      } else {
        nextSelectedPersonIds.delete(personId);
      }
      return nextSelectedPersonIds;
    });
  }

  protected selectAllDisplayedPersons(): void {
    if (!this.canEditGroupMembers()) {
      return;
    }
    const displayedPersonIds = this.members()
      .map((person) => person.personId)
      .filter((personId) => !this.isPersonRemoving(personId));
    this.selectedPersonIds.update(
      (selectedPersonIds) => new Set([...selectedPersonIds, ...displayedPersonIds]),
    );
  }
  protected clearSelectedPersons(): void {
    this.selectedPersonIds.set(new Set<number>());
  }

  protected removeSingleMember(personId: number): void {
    if (this.isPersonRemoving(personId) || !this.canEditGroupMembers()) {
      return;
    }

    this.dispatchRemoveMembersRequest([personId], () => {
      this.updatePersonSelection(personId, false);
    });
  }

  protected removeSelectedMembers(): void {
    if (!this.canEditGroupMembers()) {
      return;
    }
    const personIds = Array.from(this.selectedPersonIds());
    if (personIds.some((personId) => this.isPersonRemoving(personId))) {
      return;
    }

    this.dispatchRemoveMembersRequest(personIds, () => {
      this.clearSelectedPersons();
    });
  }

  private dispatchRemoveMembersRequest(personIds: number[], onSuccess: () => void): void {
    if (!this.canEditGroupMembers()) {
      return;
    }
    const group = this.group();

    if (!group || personIds.length === 0) {
      return;
    }

    const requestId = `${group._id}_${Date.now()}_${Math.random()}`;

    this.actions$
      .pipe(
        ofType(PersonGroupsActions.removePersonsFromGroupSuccess),
        filter((action) => action.requestId === requestId),
        takeUntilDestroyed(this.destroyRef),
        take(1),
      )
      .subscribe(() => {
        onSuccess();
      });

    this.store$.dispatch(
      PersonGroupsActions.requestRemovePersonsFromGroup({
        groupId: group._id,
        groupName: group.name,
        personIds,
        requestId,
      }),
    );
  }

  protected openConfirmSavedPersonRecognitionDialog(person: PersonModel.SavedPerson): void {
    if (!this.canEditGroupMembers()) {
      return;
    }
    if (
      person.trainingStatus === PersonModel.TrainingStatus.Trained ||
      this.isPersonConfirming(person.personId)
    ) {
      return;
    }

    this.personDialogService
      .openConfirmRecognition({ personId: person.personId })
      .afterClosed()
      .pipe(
        filter(Boolean),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((recognitionResult) => {
        this.store$.dispatch(
          PersonActions.trainSavedPerson({
            request: {
              personId: person.personId,
              positiveTrackerIds: recognitionResult.positiveTrackerIds,
              negativeTrackerIds: recognitionResult.negativeTrackerIds,
            },
          }),
        );
      });
  }

  protected isPersonConfirming(personId: number): boolean {
    return this.confirmingPersonIds().includes(personId);
  }

  protected isPersonRemoving(personId: number): boolean {
    return this.removingPersonIds().includes(personId);
  }

  protected hasRemovingSelectedPersons(): boolean {
    return Array.from(this.selectedPersonIds()).some((personId) => this.isPersonRemoving(personId));
  }

}
